import { memo, useMemo } from 'react';
import { Col, Flex, Form, Row } from 'antd';
import { FormikProps } from 'formik';

import {
  MDatePicker,
  MInput,
  MRadio,
  MSelect,
  MTextArea,
} from '@Components/Base';
import { Option } from '@Components/types/options';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import { LeaveRequestFormValues } from '@Features/EmployeeSelfService/requests/LeaveRequest/form/type';
import { LeaveTypeCodes } from '@Enum/index';
import dayjs from 'dayjs';
import MUpload from '@Components/Base/Upload/Upload';
import { Attachment } from '@Components/types/Attachment';
import { isMobileNumberValid } from '@Utils/isMobileNumberValid';
import { prepareDatePickerValue } from '@Utils/transformDateTime';
import { Balance } from '@Types/api/form/leaveRequest/Balance';

import styles from './leave-details.module.css';
import { getReimbursementTicketHint, getStartDateHint } from '../../utils';

interface LeaveDetailsSectionProps {
  formik: FormikProps<LeaveRequestFormValues>;
  label: string;
  formApproval: boolean;
  leaveTypeOptions: Option[];
  ticketOptions: Option[];
  passportOptions: Option[];
  eligibleLeaveDaysText?: string;
  eligibleTicketsText?: string;
  onFileRequest?: (options: RcCustomRequestOptions) => void;
  onFileRemove?: () => void;
  uploadedFile?: Attachment;
  onUploadedFileRemove?: (value: boolean | undefined) => void;
  balance: Balance;
}

const LeaveDetailsSection: React.FC<LeaveDetailsSectionProps> = ({
  formik,
  formApproval,
  leaveTypeOptions,
  ticketOptions,
  passportOptions,
  onFileRequest,
  onFileRemove,
  uploadedFile,
  onUploadedFileRemove,
  balance,
}) => {
  const {
    startDate: startDateTouched,
    endDate: endDateTouched,
    remarks: remarksTouched,
    document: documentTouched,
  } = formik.touched;
  const {
    startDate: startDateError,
    endDate: endDateError,
    remarks: remarksError,
    document: documentError,
  } = formik.errors;

  const isEmergencyLeaveType =
    Number(formik.values.leaveTypeID) === LeaveTypeCodes.EMERGENCY_LEAVE;

  const isSickLeaveType =
    Number(formik.values.leaveTypeID) === LeaveTypeCodes.SICK_LEAVE;

  const helpMessage = useMemo(() => {
    if (formik.values.startDate && formik.values.endDate) {
      if (
        Number(formik.values.leaveTypeID) === LeaveTypeCodes.EMERGENCY_LEAVE
      ) {
        return 'Leave will be deduct from available Annual leave';
      }
      if (Number(formik.values.totDays) > balance.asOnDate) {
        // const rest = balance.asOnDate - Number(formik.values.totDays);
        // return `Leave will be deduct as ${balance.asOnDate} Annual day(s) and  ${Math.abs(rest)}  day(s) as absent`;
        return 'You are exceeding your available annual leave days. Excess days will be booked as Emergency leave without pay';
      }
    }
    return undefined;
  }, [
    balance.asOnDate,
    formik.values.endDate,
    formik.values.leaveTypeID,
    formik.values.startDate,
    formik.values.totDays,
  ]);

  const isAnnualLeave =
    Number(formik.values.leaveTypeID) === LeaveTypeCodes.ANNUAL_LEAVE;

  const ticketReimbursementHint = getReimbursementTicketHint(
    isAnnualLeave,
    formik.values.startDate,
    balance
  );

  const startDateHint = getStartDateHint(
    formik.values.leaveTypeID,
    formik.values.startDate,
    balance
  );

  const isTicketBalanceEmpty = !balance.eligibleTickets;

  const isRangeDateNotAcceptableForTicket = useMemo(() => {
    if (
      Number(formik.values.leaveTypeID) === LeaveTypeCodes.ANNUAL_LEAVE &&
      formik.values.totDays
    ) {
      if (Number(formik.values.totDays) >= 7) {
        return false;
      }
    }
    formik.setFieldValue('ticketRequired', 0);
    return true;
  }, [formik.values.leaveTypeID, formik.values.totDays]);

  return (
    <Row className={styles.leaveDetailsSection}>
      <Col span={24}>
        <Form.Item label="Leave Type">
          <MSelect
            labeloption="Leave Type"
            value={formik.values.leaveTypeID}
            options={leaveTypeOptions}
            onChange={(selectValue) =>
              formik.setFieldValue('leaveTypeID', selectValue)
            }
            disabled={formApproval}
            placeholder="Select Leave Type"
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          label="Leave Start Date"
          validateStatus={startDateTouched && startDateError ? 'error' : ''}
          help={
            startDateTouched && startDateError ? startDateError : startDateHint
          }
        >
          <MDatePicker
            className="leave-deatails-datepicker"
            name="startDate"
            value={
              formik.values.startDate &&
              dayjs(formik.values.startDate).tz('UTC')
            }
            format="DD.MM.YYYY"
            onChange={(value) => {
              formik.setFieldValue(
                'startDate',
                prepareDatePickerValue(value, true)
              );
            }}
            onBlur={() => {
              formik.setFieldTouched('startDate', true);
            }}
            disabledDate={(current) => {
              if (isAnnualLeave) {
                return current && current < dayjs().startOf('day');
              }

              return false;
            }}
            disabled={formApproval}
            placeholder="Select Date"
          />
        </Form.Item>
      </Col>
      <Col
        span={11}
        style={{
          insetInlineStart: '2%',
          maxWidth: '47.833333%',
          flex: '0 0 47.833333%',
        }}
      >
        <Form.Item
          label="Leave End Date"
          validateStatus={endDateTouched && endDateError ? 'error' : ''}
          help={endDateTouched && endDateError ? endDateError : ''}
        >
          <MDatePicker
            className="leave-deatails-datepicker"
            name="endDate"
            value={
              formik.values.endDate && dayjs(formik.values.endDate).tz('UTC')
            }
            format="DD.MM.YYYY"
            onChange={(value) => {
              formik.setFieldValue(
                'endDate',
                prepareDatePickerValue(value, true)
              );
            }}
            onBlur={() => {
              formik.setFieldTouched('endDate', true);
            }}
            disabledDate={(current) => {
              if (formik.values.startDate) {
                // disable days less than chosen startDate
                return (
                  current &&
                  current < dayjs(formik.values.startDate).startOf('day')
                );
              }

              if (isAnnualLeave) {
                return current && current < dayjs().startOf('day');
              }

              return false;
            }}
            disabled={formApproval}
            placeholder="Select Date"
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Total no. of days" help={helpMessage}>
          <MInput
            className="leave-details-input"
            name="totDays"
            value={
              formik.values.startDate && formik.values.endDate
                ? formik.values.totDays
                : ''
            }
            disabled
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        {isSickLeaveType && (
          <Form.Item
            label="Medical Certificate"
            validateStatus={documentError && documentTouched ? 'error' : ''}
            help={documentError && documentTouched ? documentError : ''}
          >
            <MUpload
              onFileRequest={onFileRequest}
              onFileRemove={onFileRemove}
              uploadedFile={uploadedFile}
              onUploadedFileRemove={onUploadedFileRemove}
              disabled={formApproval}
            />
          </Form.Item>
        )}
      </Col>
      <Col span={24}>
        <Form.Item
          label="Remarks"
          validateStatus={remarksTouched && remarksError ? 'error' : ''}
          help={remarksTouched && remarksError ? remarksError : ''}
        >
          <MTextArea
            name="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            onBlur={() => {
              formik.setFieldTouched('remarks', true);
            }}
            rows={4}
            showCount
            maxLength={100}
            disabled={formApproval}
            placeholder="Enter your remarks..."
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Ticket Reimbursement Required">
          <Flex vertical>
            <div className={styles.ticketHint}>{ticketReimbursementHint}</div>
            <Flex>
              {ticketOptions.map((ticket) => (
                <MRadio
                  disabled={
                    isTicketBalanceEmpty ||
                    isSickLeaveType ||
                    isEmergencyLeaveType ||
                    isRangeDateNotAcceptableForTicket
                  }
                  key={ticket.label}
                  id={ticket.label}
                  value={ticket.value}
                  name="ticket"
                  labelText={ticket.label}
                  onChange={(event) =>
                    formik.setFieldValue('ticketRequired', event.target.value)
                  }
                  checked={
                    Number(formik.values.ticketRequired) ===
                    Number(ticket.value)
                  }
                />
              ))}
            </Flex>
          </Flex>
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Passport Required">
          <Flex>
            {passportOptions.map((passportItem) => (
              <MRadio
                key={passportItem.label}
                id={passportItem.label}
                value={passportItem.value}
                name="passport"
                labelText={passportItem.label}
                onChange={(event) =>
                  formik.setFieldValue('passportRequired', event.target.value)
                }
                checked={
                  Number(formik.values.passportRequired) ===
                  Number(passportItem.value)
                }
              />
            ))}
          </Flex>
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Home Country Contact Number">
          <MInput
            className="leave-details-input"
            name="telephone"
            value={formik.values.telephone}
            onChange={(e) => {
              const NUMBER_LENGTH_LIMIT = 18;

              const { value } = e.target;
              const isNumberValid = isMobileNumberValid(
                value,
                NUMBER_LENGTH_LIMIT
              );

              if (isNumberValid) {
                formik.setFieldValue('telephone', value);
              }
            }}
            disabled={formApproval}
            placeholder="Insert number"
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export default memo(LeaveDetailsSection);
